import { addBusinessDays, REVISION_DEADLINE_BUSINESS_DAYS } from './dateUtils';

export { REVISION_DEADLINE_BUSINESS_DAYS } from './dateUtils';
export const ACCEPT_DEADLINE_BUSINESS_DAYS = 2;
export const REVIEW_DEADLINE_BUSINESS_DAYS = 5;

export function isProductCreator(creator) {
  return (
    String(creator?.productType || '').toLowerCase() === 'product' ||
    creator?.serviceOrProduct === 'Product'
  );
}

export function isProductReceived(creator) {
  return !!(creator?.progress?.productReceived || creator?.productReceived);
}

export function isAwaitingProduct(creator) {
  return isProductCreator(creator) && !isProductReceived(creator);
}

function toDate(value) {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (value.toDate) return value.toDate();
  return new Date(value);
}

function formatTimeLeft(targetDate) {
  const timeLeft = targetDate.getTime() - Date.now();
  const hoursLeft = Math.ceil(timeLeft / (1000 * 60 * 60));
  const daysLeft = Math.ceil(timeLeft / (1000 * 60 * 60 * 24));

  if (timeLeft <= 0) {
    return { dueText: 'Overdue', isOverdue: true, daysLeft: 0, hoursLeft: 0 };
  }
  if (hoursLeft < 24) {
    return { dueText: `${hoursLeft}h`, isOverdue: false, daysLeft, hoursLeft };
  }
  return { dueText: `${daysLeft}d`, isOverdue: false, daysLeft, hoursLeft };
}

/**
 * Single source of truth for creator due countdown + overdue flag on the brand dashboard.
 * Used by the campaign creator row AND the progress modal so they always agree.
 */
export function getCreatorDueInfo(creator) {
  const status = creator?.status || '';

  if (status === 'awaiting_approval') {
    if (!creator.assignedAt) {
      return { dueText: '2d', isOverdue: false, dueLabel: 'Pending Acceptance' };
    }
    const acceptDeadline = addBusinessDays(new Date(creator.assignedAt), ACCEPT_DEADLINE_BUSINESS_DAYS);
    const result = formatTimeLeft(acceptDeadline);
    if (result.isOverdue) {
      return { dueText: 'Expired', isOverdue: true, dueLabel: 'Pending Acceptance' };
    }
    return { dueText: result.dueText, isOverdue: false, dueLabel: 'Pending Acceptance' };
  }

  // Brand has requested a revision: the clock now belongs to the CREATOR (time to
  // resubmit), not the brand's review window. Counting the review window here is
  // misleading — the brand isn't reviewing anything until the creator reships.
  if (creator.submissionData?.status === 'needs_revision') {
    const revisionRequestedAt = toDate(
      creator.submissionData.lastRevisionRequestedAt || creator.submissionData.submittedAt
    );
    if (!revisionRequestedAt) {
      return { dueText: `${REVISION_DEADLINE_BUSINESS_DAYS}d`, isOverdue: false, dueLabel: 'Awaiting creator revision' };
    }
    const revisionDeadline = addBusinessDays(revisionRequestedAt, REVISION_DEADLINE_BUSINESS_DAYS);
    const result = formatTimeLeft(revisionDeadline);
    return {
      dueText: result.dueText,
      isOverdue: result.isOverdue,
      dueLabel: result.isOverdue ? 'Creator revision overdue' : `Creator revision due in ${result.dueText}`,
    };
  }

  const inReview =
    (creator.hasSubmission || creator.submissionData) &&
    (status === 'submitted' || creator.progress?.submitted === true || creator.hasSubmission);

  if (inReview && creator.submissionData) {
    const submittedAt = toDate(
      creator.submissionData.submittedAt?.toDate?.() ?? creator.submissionData.submittedAt
    );
    const reviewDueDate =
      toDate(creator.submissionData.dueDate) ||
      (submittedAt ? addBusinessDays(submittedAt, REVIEW_DEADLINE_BUSINESS_DAYS) : null);

    if (!reviewDueDate) {
      return {
        dueText: `${REVIEW_DEADLINE_BUSINESS_DAYS}d`,
        isOverdue: false,
        dueLabel: `Review: ${REVIEW_DEADLINE_BUSINESS_DAYS} business days`,
      };
    }

    const result = formatTimeLeft(reviewDueDate);
    return {
      dueText: result.dueText,
      isOverdue: result.isOverdue,
      dueLabel: result.isOverdue ? 'Review overdue' : `Review due in ${result.dueText}`,
    };
  }

  // Product clock hasn't started — never show overdue while waiting on shipment/receipt.
  if (isAwaitingProduct(creator)) {
    return { dueText: 'TBD', isOverdue: false, dueLabel: 'Awaiting Product' };
  }

  const effectiveDeadline = creator.extendedDeadline || creator.deadline;
  if (effectiveDeadline) {
    const deadline = toDate(effectiveDeadline);
    const result = formatTimeLeft(deadline);
    const dueText = result.isOverdue
      ? 'Overdue'
      : result.dueText.endsWith('d')
        ? `${result.daysLeft} days`
        : result.dueText;

    return {
      dueText,
      isOverdue: result.isOverdue,
      dueLabel: result.isOverdue ? 'Overdue' : `Due in ${result.dueText}`,
    };
  }

  if (isProductCreator(creator)) {
    return { dueText: 'TBD', isOverdue: false, dueLabel: 'Awaiting Product' };
  }

  return { dueText: '7 days', isOverdue: false, dueLabel: 'Due: 7 days' };
}

/** Human-readable status label for the creator assignment row. */
export function getCreatorStatusLabel(creator) {
  if (creator.submissionData?.status === 'needs_revision') return 'Awaiting Revision';
  if (creator.hasSubmission) return 'Submitted';
  if (creator.status === 'cancelled') return 'Cancelled';
  if (creator.status === 'awaiting_approval') return 'Pending';

  const isProduct = isProductCreator(creator);
  const isProductSent =
    creator.status === 'product_sent' || creator.progress?.productSent === true;
  const productReceived = isProductReceived(creator);

  if (isProduct && isProductSent && !productReceived) return 'Awaiting Confirmation';
  if (
    isProduct &&
    !productReceived &&
    ['accepted', 'confirmed', 'awaiting_product'].includes(creator.status)
  ) {
    return 'Awaiting Product';
  }
  if (creator.status === 'filming') return 'Filming';
  if (creator.status === 'editing') return 'Editing';
  if (creator.status === 'submitted') return 'Submitted';
  if (creator.status === 'rejected') return 'Rejected';
  return 'In Progress';
}

/** CSS modifier class for the status pill on the creator row. */
export function getCreatorStatusCssClass(creator) {
  if (creator.submissionData?.status === 'needs_revision') return 'awaiting_revision';
  if (creator.hasSubmission) return 'submitted';
  if (creator.status === 'awaiting_approval') return 'awaiting_approval';
  if (creator.status === 'cancelled') return 'cancelled';

  const isProduct = isProductCreator(creator);
  const isProductSent =
    creator.status === 'product_sent' || creator.progress?.productSent === true;
  const productReceived = isProductReceived(creator);

  if (isProduct && isProductSent && !productReceived) return 'awaiting_receive';
  if (
    isProduct &&
    !productReceived &&
    ['accepted', 'confirmed', 'awaiting_product'].includes(creator.status)
  ) {
    return 'awaiting_product';
  }
  if (creator.status === 'filming') return 'filming';
  if (creator.status === 'editing') return 'editing';
  return creator.status || 'awaiting_approval';
}
